import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Notification from '../components/Notification';
import './Register.css';

function Register() {
  const { registerStudent } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    address: '',
    sex: '',
    contactNumber: '',
    email: '',
    course: '',
    emergencyContactName: '',
    emergencyContactNumber: '',
    emergencyContactAddress: '',
    nstpComponent: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    // Check for required fields
    const requiredFields = [
      'firstName', 'lastName', 'address', 'contactNumber', 'email', 
      'course', 'nstpComponent',
      'emergencyContactName', 'emergencyContactNumber'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        addNotification('error', `${field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())} is required`);
        return false;
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      addNotification('error', 'Please enter a valid email address');
      return false;
    }

    return true;
  };

  const addNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setNotifications([]);
      setLoading(true);
      
      // Create student data object
      const studentData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName || '',
        address: formData.address,
        sex: formData.sex || '',
        contactNumber: formData.contactNumber,
        email: formData.email,
        course: formData.course,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactNumber: formData.emergencyContactNumber,
        emergencyContactAddress: formData.emergencyContactAddress || '',
        nstpComponent: formData.nstpComponent,
      };
      
      // Register the student in the database
      const result = await registerStudent(studentData);
      
      // Add a message to show the registration was successful
      addNotification('success', 'Registration successful! Thank you for registering for the NSTP program.');
      
      // Check if email notification was sent
      if (result.emailSent) {
        addNotification('success', 'A confirmation email has been sent to your email address.');
      } else {
        addNotification('info', 'Registration complete. We could not send an email confirmation at this time.');
      }
      
      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        middleName: '',
        address: '',
        sex: '',
        contactNumber: '',
        email: '',
        course: '',
        emergencyContactName: '',
        emergencyContactNumber: '',
        emergencyContactAddress: '',
        nstpComponent: '',
      });
      
    } catch (err) {
      console.error(err);
      addNotification('error', 'Failed to register: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="card register-card">
        <h2 className="page-title">National Service Training Program (NSTP) Competent Enlistment Form</h2>
        <p className="form-description">
          (The National Service Training Program (NSTP), is a civic education and defense preparedness program 
          for students instituted by the Government of the Philippines on 5 January 2002 by virtue of Republic Act 
          9163, otherwise known as the "National Service Training Program (NSTP) Act 2001.")
        </p>
        
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            type={notification.type}
            message={notification.message}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Name of Student:</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="lastName">Family Name*</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-control"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="firstName">First Name*</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-control"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="middleName">Middle Name</label>
                <input
                  type="text"
                  id="middleName"
                  name="middleName"
                  className="form-control"
                  value={formData.middleName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="address">Address*</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  className="form-control"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="sex">Gender*</label>
                <select
                  id="sex"
                  name="sex"
                  className="form-control"
                  value={formData.sex}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="contactNumber">Contact Number*</label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  className="form-control"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="course">Course*</label>
                <select
                  id="course"
                  name="course"
                  className="form-control"
                  value={formData.course}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="SOICT">SOICT</option>
                  <option value="SOBM">SOBM</option>
                  <option value="SOHM">SOHM</option>
                  <option value="SOCJ">SOCJ</option>
                  <option value="SOTE">SOTE</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="email">E-mail Address*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group full-width">
                <label className="emergency-label">Person to Contact in Case of Emergency:</label>
              </div>

              <div className="form-group full-width">
                <label htmlFor="emergencyContactName">Name*</label>
                <input
                  type="text"
                  id="emergencyContactName"
                  name="emergencyContactName"
                  className="form-control"
                  value={formData.emergencyContactName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="emergencyContactNumber">Contact Number*</label>
                <input
                  type="tel"
                  id="emergencyContactNumber"
                  name="emergencyContactNumber"
                  className="form-control"
                  value={formData.emergencyContactNumber}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="emergencyContactAddress">Address</label>
                <input
                  type="text"
                  id="emergencyContactAddress"
                  name="emergencyContactAddress"
                  className="form-control"
                  value={formData.emergencyContactAddress}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Coverage</h3>
            <p className="component-description">
              Under the NSTP Program, both male and female college students of any baccalaureate degree course or 
              technical-vocational courses in public and private educational institutions are obliged to complete one of the NSTP 
              components for an academic period of two semesters. The students, however, are free to choose which 
              particular program component they would want to serve.
            </p>
            
            <p className="component-instruction">
              As a student of Pasu City College, I hereby commit/undertake to be enlisted in - (Please put a (✓) 
              check mark before your chosen component. Only one (1) component must be chosen.)
            </p>
            
            <div className="nstp-options">
              <div className="form-group radio-group">
                <input
                  type="radio"
                  id="cwts"
                  name="nstpComponent"
                  value="CWTS"
                  checked={formData.nstpComponent === 'CWTS'}
                  onChange={handleChange}
                />
                <label htmlFor="cwts">
                  <strong>Civic Welfare Training Service (CWTS)</strong>
                  <p>This program component is designed to provide students with activities contributory to the general 
                     welfare and betterment of life of the members of the community especially those relating to improving 
                     health, education, environment, entrepreneurship, safety, recreation and morals of the citizenry and other 
                     social welfare services.</p>
                </label>
              </div>
              
              <div className="form-group radio-group">
                <input
                  type="radio"
                  id="rotc"
                  name="nstpComponent"
                  value="ROTC"
                  checked={formData.nstpComponent === 'ROTC'}
                  onChange={handleChange}
                />
                <label htmlFor="rotc">
                  <strong>Reserve Officers' Training Corps (ROTC)</strong>
                  <p>This program component is designed to provide military education and training for students to 
                     mobilize them for national defense preparedness. This is also a glimpse for young people to see 
                     how military life is and its discipline.</p>
                </label>
              </div>
            </div>
          </div>
          
          <div className="form-footer">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Submitting Registration...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Register; 